// Re-export core types from shared package
export type {
  Project,
  ProjectPhase,
  ProjectPhaseId,
  ProjectPhaseConfig,
  ProjectDocument,
  CreateProjectInput,
  UpdateProjectInput,
} from '@molt-starter/shared';

export {
  PROJECT_PHASE_IDS,
  PROJECT_PHASES_CONFIG,
  getPhaseConfig,
  getPhaseConfigById,
  getAllPhaseKeys,
  isValidPhase,
} from '@molt-starter/shared';

import type { ProjectPhase } from '@molt-starter/shared';
import { PROJECT_PHASES_CONFIG } from '@molt-starter/shared';

/**
 * UI-specific phase styling (Tailwind classes)
 * Extends the core phase config with frontend styling
 */
export interface ProjectPhaseUIConfig {
  // Tailwind classes for styling
  bgColor: string;
  textColor: string;
  borderColor: string;
  // Dark mode variants
  darkBgColor: string;
  darkTextColor: string;
  darkBorderColor: string;
}

/**
 * UI styling for each project phase
 */
export const PROJECT_PHASES_UI: Record<ProjectPhase, ProjectPhaseUIConfig> = {
  IDEATION: {
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    darkBgColor: 'dark:bg-amber-900/30',
    darkTextColor: 'dark:text-amber-400',
    darkBorderColor: 'dark:border-amber-500/30',
  },
  DEVELOPMENT: {
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
    darkBgColor: 'dark:bg-blue-900/30',
    darkTextColor: 'dark:text-blue-400',
    darkBorderColor: 'dark:border-blue-500/30',
  },
  PRESENTATION: {
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200',
    darkBgColor: 'dark:bg-purple-900/30',
    darkTextColor: 'dark:text-purple-400',
    darkBorderColor: 'dark:border-purple-500/30',
  },
  DELIVERED: {
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
    darkBgColor: 'dark:bg-green-900/30',
    darkTextColor: 'dark:text-green-400',
    darkBorderColor: 'dark:border-green-500/30',
  },
  ARCHIVED: {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
    darkBgColor: 'dark:bg-gray-800/50',
    darkTextColor: 'dark:text-gray-400',
    darkBorderColor: 'dark:border-gray-600/30',
  },
};

/**
 * Combined phase config (core + UI) for convenience
 * This merges the shared core config with frontend-specific UI styling
 */
export const PROJECT_PHASES = Object.fromEntries(
  (Object.keys(PROJECT_PHASES_CONFIG) as ProjectPhase[]).map((key) => [
    key,
    {
      ...PROJECT_PHASES_CONFIG[key],
      ...PROJECT_PHASES_UI[key],
    },
  ])
) as Record<
  ProjectPhase,
  (typeof PROJECT_PHASES_CONFIG)[ProjectPhase] & ProjectPhaseUIConfig
>;

/**
 * Get remaining time text for a phase
 */
export const getPhaseTimeRemaining = (
  phase: ProjectPhase,
  startDate: Date,
  currentDate: Date = new Date()
): string | null => {
  const config = PROJECT_PHASES_CONFIG[phase];
  if (!config.durationDays) return null;

  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + config.durationDays);

  const diffMs = endDate.getTime() - currentDate.getTime();
  if (diffMs <= 0) return 'Ending soon';

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(
    (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );

  if (diffDays > 0) return `${diffDays}d ${diffHours}h left`;
  return `${diffHours}h left`;
};
